/**
 * InspectionFlow.js — Inspection contingency workflow component.
 * Upload report → AI analysis → Review issues → Submit repair/credit request → Track seller response.
 */

import React, { useState, useRef } from 'react';
import { useInspectionIssues, useNegotiationHistory } from '../../agents/contingencyAgent';

const SEVERITY_ORDER = { critical: 0, major: 1, minor: 2, cosmetic: 3 };
const SEVERITY_LABELS = { critical: 'Critical', major: 'Major', minor: 'Minor', cosmetic: 'Cosmetic' };
const ACTION_LABELS = { repair: 'Request Repair', credit: 'Request Credit', accept: 'Accept As-Is' };

const INSPECTION_FLOW_STEPS = [
  { key: 'upload', label: 'Upload Report', statuses: ['active', 'report_uploaded', 'analyzing', 'analysis_complete', 'repair_requested', 'repair_responded', 'repair_agreed', 'satisfied'] },
  { key: 'review', label: 'Review Issues', statuses: ['analysis_complete', 'repair_requested', 'repair_responded', 'repair_agreed', 'satisfied'] },
  { key: 'request', label: 'Send Request', statuses: ['repair_requested', 'repair_responded', 'repair_agreed', 'satisfied'] },
  { key: 'resolve', label: 'Resolve', statuses: ['repair_agreed', 'satisfied'] },
];

function getInspectionFlowIndex(contingencyStatus) {
  const idx = INSPECTION_FLOW_STEPS.findIndex((step) => step.statuses.includes(contingencyStatus));
  return idx === -1 ? 0 : idx;
}

export default function InspectionFlow({ contingency, onRefresh }) {
  const fileRef = useRef(null);
  const { issues, loading, error, saving, totals, uploadReport, submitRequest, addIssue } = useInspectionIssues(contingency?.id);
  const { history } = useNegotiationHistory(contingency?.transaction_id);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [decisions, setDecisions] = useState({}); // issueId → {action, amount}
  const [notes, setNotes] = useState('');
  const [uploadMsg, setUploadMsg] = useState(null);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualIssue, setManualIssue] = useState({ description: '', severity: 'major', estimated_cost: '' });
  const [manualMsg, setManualMsg] = useState(null);
  const flowIndex = getInspectionFlowIndex(contingency?.status);

  const sortedIssues = [...issues].sort((a, b) =>
    (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
  );

  // Auto-show manual form when AI parsing completed but found no issues
  const parseFailed = contingency?.status === 'analysis_complete' && !loading && sortedIssues.length === 0;

  const handleAddIssue = async () => {
    if (!manualIssue.description.trim()) {
      setManualMsg({ type: 'error', text: 'Description is required.' });
      return;
    }
    const issueData = {
      description: manualIssue.description.trim(),
      severity: manualIssue.severity,
    };
    if (manualIssue.estimated_cost) {
      issueData.estimated_cost = parseFloat(manualIssue.estimated_cost);
    }
    const result = await addIssue(contingency?.transaction_id, issueData);
    if (result.success) {
      setManualMsg({ type: 'success', text: 'Issue added.' });
      setManualIssue({ description: '', severity: 'major', estimated_cost: '' });
      setShowManualForm(false);
    } else {
      setManualMsg({ type: 'error', text: result.error });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIssues(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDecisions(prev => {
      if (prev[id]) return prev;
      return { ...prev, [id]: { action: 'repair', amount: '' } };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadMsg({ type: 'info', text: 'Uploading report…' });
    const result = await uploadReport(file);
    if (result.success) {
      setUploadMsg({ type: 'success', text: 'Report uploaded! AI is analyzing it now — refresh in ~30 seconds.' });
      onRefresh?.();
    } else {
      setUploadMsg({ type: 'error', text: result.error });
    }
    e.target.value = '';
  };

  const handleSubmitRequest = async () => {
    if (selectedIssues.size === 0) {
      setSubmitMsg({ type: 'error', text: 'Select at least one issue.' });
      return;
    }
    const issueDecisions = Array.from(selectedIssues).map(id => ({
      issue_id: id,
      buyer_action: decisions[id]?.action || 'repair',
      requested_amount: decisions[id]?.amount ? parseFloat(decisions[id].amount) : null,
    }));
    const result = await submitRequest(issueDecisions, notes);
    if (result.success) {
      setSubmitMsg({ type: 'success', text: 'Repair/credit request submitted to seller.' });
      setSelectedIssues(new Set());
      setDecisions({});
      setNotes('');
      onRefresh?.();
    } else {
      setSubmitMsg({ type: 'error', text: result.error });
    }
  };

  const canSubmitRequest = !['repair_requested', 'repair_agreed', 'satisfied', 'removed', 'expired', 'exercised'].includes(contingency?.status);

  return (
    <div>
      <div className="ch-card">
        <div className="ch-card-title">Inspection Workflow</div>
        <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
          Follow these steps in order to keep negotiations clear and on time.
        </p>
        <div className="ch-flow-steps" role="list" aria-label="Inspection contingency workflow progress">
          {INSPECTION_FLOW_STEPS.map((step, index) => {
            const isComplete = index < flowIndex;
            const isActive = index === flowIndex;
            return (
              <div
                key={step.key}
                className={`ch-flow-step${isComplete ? ' is-complete' : ''}${isActive ? ' is-active' : ''}`}
                role="listitem"
              >
                <span className="ch-flow-step-dot" aria-hidden="true">{isComplete ? '✓' : index + 1}</span>
                <span className="ch-flow-step-label">{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload section */}
      {!issues.length && (
        <div className="ch-card">
          <div className="ch-card-title">Step 1 — Upload Inspection Report</div>
          {contingency?.status === 'analyzing' && (
            <div className="ch-deadline-banner medium">AI is analyzing the inspection report. Refresh in a moment.</div>
          )}
          {parseFailed && (
            <div className="ch-deadline-banner medium" style={{ marginBottom: '0.75rem' }}>
              AI could not extract issues automatically. Add them manually below.
            </div>
          )}
          {contingency?.status === 'active' || contingency?.status === 'report_uploaded' || contingency?.status === 'analysis_complete' ? (
            <>
              <div className="ch-form-row">
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
                  Upload your inspection report (PDF or image, max 25 MB). Our AI will automatically extract and categorize all issues.
                </p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  ref={fileRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button className="ch-btn ch-btn-primary" onClick={() => fileRef.current?.click()} disabled={saving || contingency?.status === 'analyzing'}>
                    {saving ? 'Uploading…' : 'Choose Report File'}
                  </button>
                  {!parseFailed && (
                    <button
                      className="ch-btn ch-btn-ghost"
                      onClick={() => setShowManualForm(v => !v)}
                    >
                      Add issue manually
                    </button>
                  )}
                </div>
              </div>
              {uploadMsg && (
                <div className={`ch-deadline-banner ${uploadMsg.type === 'error' ? 'critical' : uploadMsg.type === 'success' ? 'low' : 'medium'}`}>
                  {uploadMsg.text}
                </div>
              )}
            </>
          ) : null}

          {/* Manual issue form — shown when parse failed or user toggled */}
          {(parseFailed || showManualForm) && (
            <div style={{ marginTop: '1rem', borderTop: parseFailed ? 'none' : '1px solid var(--border)', paddingTop: parseFailed ? 0 : '1rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Add Issue Manually</div>
              <div className="ch-form-row">
                <label className="ch-form-label">Description</label>
                <textarea
                  className="ch-textarea"
                  placeholder="Describe the inspection issue…"
                  value={manualIssue.description}
                  onChange={e => setManualIssue(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div className="ch-form-row" style={{ flex: 1, minWidth: 140 }}>
                  <label className="ch-form-label">Severity</label>
                  <select
                    className="ch-select"
                    value={manualIssue.severity}
                    onChange={e => setManualIssue(prev => ({ ...prev, severity: e.target.value }))}
                  >
                    <option value="critical">Critical</option>
                    <option value="major">Major</option>
                    <option value="minor">Minor</option>
                  </select>
                </div>
                <div className="ch-form-row" style={{ flex: 1, minWidth: 140 }}>
                  <label className="ch-form-label">Estimated Cost ($, optional)</label>
                  <input
                    className="ch-input"
                    type="number"
                    min="0"
                    placeholder="e.g. 2500"
                    value={manualIssue.estimated_cost}
                    onChange={e => setManualIssue(prev => ({ ...prev, estimated_cost: e.target.value }))}
                  />
                </div>
              </div>
              {manualMsg && (
                <div className={`ch-deadline-banner ${manualMsg.type === 'error' ? 'critical' : 'low'}`} style={{ marginBottom: '0.5rem' }}>
                  {manualMsg.text}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="ch-btn ch-btn-primary" onClick={handleAddIssue} disabled={saving || !manualIssue.description.trim()}>
                  {saving ? 'Adding…' : 'Add Issue'}
                </button>
                {!parseFailed && (
                  <button className="ch-btn ch-btn-ghost" onClick={() => { setShowManualForm(false); setManualMsg(null); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Issue list */}
      {loading && <div className="ch-loading">Loading issues…</div>}
      {error && <div className="ch-error">{error}</div>}

      {sortedIssues.length > 0 && (
        <div className="ch-card">
          <div className="ch-card-title">
            Step 2 — Review Issues ({sortedIssues.length})
            <span style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
              Est. cost: ${totals.costLow.toLocaleString()}–${totals.costHigh.toLocaleString()}
            </span>
          </div>

          {/* Negotiation history strip */}
          {history && history.current_round > 0 && (
            <div className="ch-neg-history-strip">
              <span>Round {history.current_round} of negotiations</span>
              <span className="ch-neg-divider" />
              <span>Requested: <strong>${(history.total_requested || 0).toLocaleString()}</strong></span>
              <span className="ch-neg-divider" />
              <span>Agreed: <strong>${(history.total_agreed || 0).toLocaleString()}</strong></span>
            </div>
          )}

          {/* Cost summary badges */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {Object.entries(totals.bySeverity).filter(([, v]) => v > 0).map(([sev, count]) => (
              <span key={sev} className={`ch-issue-severity ${sev}`}>{count} {sev}</span>
            ))}
          </div>

          <div className="ch-issue-list">
            {sortedIssues.map(issue => {
              const isSel = selectedIssues.has(issue.id);
              const dec = decisions[issue.id] || {};
              return (
                <div
                  key={issue.id}
                  className={`ch-issue-row${isSel ? ' selected' : ''}`}
                  onClick={canSubmitRequest ? () => toggleSelect(issue.id) : undefined}
                  style={canSubmitRequest ? { cursor: 'pointer' } : {}}
                >
                  {canSubmitRequest && (
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSelect(issue.id)}
                      onClick={e => e.stopPropagation()}
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    />
                  )}
                  <span className={`ch-issue-severity ${issue.severity}`}>
                    {SEVERITY_LABELS[issue.severity] || issue.severity}
                  </span>
                  <div className="ch-issue-content">
                    <div className="ch-issue-title">{issue.title}</div>
                    {issue.description && <div className="ch-issue-desc">{issue.description}</div>}
                    <div className="ch-issue-cost">
                      Est. ${(issue.estimated_cost_low || 0).toLocaleString()}–${(issue.estimated_cost_high || 0).toLocaleString()}
                    </div>
                    {issue.ai_explanation && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.25rem', fontStyle: 'italic' }}>
                        {issue.ai_explanation}
                      </div>
                    )}
                    {/* Seller response */}
                    {issue.seller_response && (
                      <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', fontWeight: 600, color: issue.seller_response === 'accept' ? 'var(--green)' : 'var(--accent)' }}>
                        Seller: {issue.seller_response === 'accept' ? 'Agreed' : issue.seller_response === 'reject' ? 'Declined' : issue.seller_response}
                        {issue.seller_response_amount && ` ($${parseFloat(issue.seller_response_amount).toLocaleString()})`}
                        {issue.seller_response_notes && ` — ${issue.seller_response_notes}`}
                      </div>
                    )}
                    {/* Inline action picker when selected */}
                    {isSel && canSubmitRequest && (
                      <div
                        style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <select
                          className="ch-select"
                          style={{ width: 'auto' }}
                          value={dec.action || 'repair'}
                          onChange={e => setDecisions(prev => ({ ...prev, [issue.id]: { ...prev[issue.id], action: e.target.value } }))}
                        >
                          {Object.entries(ACTION_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                        {dec.action === 'credit' && (
                          <input
                            type="number"
                            className="ch-input"
                            style={{ width: '120px' }}
                            placeholder="$ amount"
                            value={dec.amount || ''}
                            onChange={e => setDecisions(prev => ({ ...prev, [issue.id]: { ...prev[issue.id], amount: e.target.value } }))}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit block */}
          {canSubmitRequest && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
              {selectedIssues.size === 0 && (
                <div className="ch-deadline-banner medium" style={{ marginBottom: '0.75rem' }}>
                  Step 3 — Select one or more issues to build your request package.
                </div>
              )}
              {selectedIssues.size > 0 && (
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  Selected {selectedIssues.size} issue(s) for request
                </div>
              )}
              <div className="ch-form-row">
                <label className="ch-form-label">Additional notes to seller (optional)</label>
                <textarea className="ch-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Please complete all repairs before closing." />
              </div>
              {submitMsg && (
                <div className={`ch-deadline-banner ${submitMsg.type === 'error' ? 'critical' : 'low'}`} style={{ marginBottom: '0.5rem' }}>
                  {submitMsg.text}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="ch-btn ch-btn-primary" onClick={handleSubmitRequest} disabled={saving || selectedIssues.size === 0}>
                  {saving ? 'Submitting…' : `Submit Request (${selectedIssues.size} issues)`}
                </button>
                <button
                  className="ch-btn ch-btn-ghost"
                  style={{ fontSize: '0.82rem' }}
                  onClick={() => setShowManualForm(v => !v)}
                >
                  {showManualForm ? 'Hide form' : 'Add issue manually'}
                </button>
              </div>

              {/* Inline manual issue form (in issues card) */}
              {showManualForm && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Add Issue Manually</div>
                  <div className="ch-form-row">
                    <label className="ch-form-label">Description</label>
                    <textarea
                      className="ch-textarea"
                      placeholder="Describe the inspection issue…"
                      value={manualIssue.description}
                      onChange={e => setManualIssue(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div className="ch-form-row" style={{ flex: 1, minWidth: 140 }}>
                      <label className="ch-form-label">Severity</label>
                      <select
                        className="ch-select"
                        value={manualIssue.severity}
                        onChange={e => setManualIssue(prev => ({ ...prev, severity: e.target.value }))}
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                    <div className="ch-form-row" style={{ flex: 1, minWidth: 140 }}>
                      <label className="ch-form-label">Estimated Cost ($, optional)</label>
                      <input
                        className="ch-input"
                        type="number"
                        min="0"
                        placeholder="e.g. 2500"
                        value={manualIssue.estimated_cost}
                        onChange={e => setManualIssue(prev => ({ ...prev, estimated_cost: e.target.value }))}
                      />
                    </div>
                  </div>
                  {manualMsg && (
                    <div className={`ch-deadline-banner ${manualMsg.type === 'error' ? 'critical' : 'low'}`} style={{ marginBottom: '0.5rem' }}>
                      {manualMsg.text}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="ch-btn ch-btn-primary" onClick={handleAddIssue} disabled={saving || !manualIssue.description.trim()}>
                      {saving ? 'Adding…' : 'Add Issue'}
                    </button>
                    <button className="ch-btn ch-btn-ghost" onClick={() => { setShowManualForm(false); setManualMsg(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status when nothing uploaded yet and not active */}
      {!loading && !sortedIssues.length && contingency && !['active', 'report_uploaded', 'analyzing', 'analysis_complete'].includes(contingency.status) && (
        <div className="ch-empty">No inspection data yet.</div>
      )}
    </div>
  );
}
